// src/pages/BlockedNumbers/BlockedModal.jsx
// Tailwind modal (mesmo padrão do PredefinedModal)

import React, { useEffect, useState } from 'react';
import { canonicalPhone } from './BlockedNumbers.api';

export default function BlockedModal({ show, onClose, onSubmit, initial }) {
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [active, setActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setError('');
    if (initial) {
      setPhone(initial.phone || '');
      setReason(initial.reason || '');
      setActive(initial.active !== false);
      setCreatedBy(initial.createdBy || '');
      setExpiresAt(initial.expiresAt ? initial.expiresAt.slice(0, 16) : '');
    } else {
      setPhone('');
      setReason('');
      setActive(true);
      setCreatedBy('');
      setExpiresAt('');
    }
  }, [show, initial]);

  function validate() {
    const c = canonicalPhone(phone);
    if (!c || !c.startsWith('+') || c.length < 8) {
      setError('Please enter a valid E.164 phone (e.g., +15551234567).');
      return false;
    }
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSubmit({
        phone,
        reason,
        active,
        createdBy: createdBy || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      onClose(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-md w-full max-w-lg">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">
              {initial ? 'Edit Blocked Number' : 'Add Blocked Number'}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="text-sm text-red-700 bg-red-100 px-4 py-2 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (E.164)</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring focus:ring-blue-200"
                placeholder="+15551234567"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">We canonicalize to +countrycode automatically.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Do not auto-reply"
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires at (optional)</label>
              <input
                type="datetime-local"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
              />
            </div>

            <label className="flex items-center text-sm gap-2">
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={active}
                onChange={e => setActive(e.target.checked)}
              />
              Active
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created by (optional)</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="admin@company.com"
                value={createdBy}
                onChange={e => setCreatedBy(e.target.value)}
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm disabled:opacity-60"
            >
              {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
